import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Lock, CheckCircle2, XCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  // Validation du mot de passe
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0
  };

  const isPasswordValid = Object.values(passwordChecks).every(check => check);

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token manquant. Veuillez utiliser le lien complet reçu par email.');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/api/auth/verify-activation-token`, {
          token
        });

        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email || '');
          setUserName(response.data.name || '');
        } else {
          setError(response.data.message || 'Ce lien n\'est plus valide.');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        if (err.response?.status === 400) {
          setError(err.response.data?.detail || 'Ce lien a expiré ou a déjà été utilisé.');
        } else {
          setError('Erreur lors de la vérification du lien. Veuillez réessayer.');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Veuillez respecter tous les critères du mot de passe.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/set-password`, {
        token,
        password
      });

      if (response.data.success) {
        setSuccess(true);
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError(err.response?.data?.detail || 'Une erreur s\'est produite. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Page de chargement pendant la vérification du token
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-[#1db584]" />
              <p className="text-gray-600">Vérification du lien d'activation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Page de succès
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Compte activé !</h2>
              <p className="text-gray-600">
                Votre mot de passe a été défini avec succès. Vous allez être redirigé vers la page de connexion.
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-[#1db584] hover:bg-[#19a074] mt-4"
              >
                Se connecter maintenant
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Page d'erreur (token invalide)
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Lien invalide</h2>
              <p className="text-gray-600">{error}</p>
              <div className="flex flex-col gap-2 mt-4 w-full">
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-[#1db584] hover:bg-[#19a074]"
                >
                  Aller à la connexion
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/forgot-password')}
                >
                  Demander un nouveau lien
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Formulaire de définition du mot de passe
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#1db584]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-[#1db584]" />
          </div>
          <CardTitle className="text-2xl">Définir votre mot de passe</CardTitle>
          <CardDescription>
            {userName ? `Bienvenue ${userName} !` : 'Bienvenue !'} Créez un mot de passe sécurisé pour activer votre compte.
          </CardDescription>
          {userEmail && (
            <p className="text-sm text-gray-500 mt-2">
              Compte : <strong>{userEmail}</strong>
            </p>
          )}
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Entrez votre mot de passe"
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez votre mot de passe"
                  className="pl-10 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Indicateurs de validation */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">Le mot de passe doit contenir :</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className={`flex items-center gap-2 ${passwordChecks.length ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordChecks.length ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2" />}
                  Au moins 8 caractères
                </div>
                <div className={`flex items-center gap-2 ${passwordChecks.uppercase ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordChecks.uppercase ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2" />}
                  Une majuscule
                </div>
                <div className={`flex items-center gap-2 ${passwordChecks.lowercase ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordChecks.lowercase ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2" />}
                  Une minuscule
                </div>
                <div className={`flex items-center gap-2 ${passwordChecks.number ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordChecks.number ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2" />}
                  Un chiffre
                </div>
              </div>
              <div className={`flex items-center gap-2 text-sm ${passwordChecks.match ? 'text-green-600' : 'text-gray-400'}`}>
                {passwordChecks.match ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border-2" />}
                Les mots de passe correspondent
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#1db584] hover:bg-[#19a074]"
              disabled={loading || !isPasswordValid}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activation en cours...
                </>
              ) : (
                'Activer mon compte'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
